import { useLoaderData } from 'react-router';

import { cn } from '@rocco/utils/cn';
import type { loader } from '@modules/professional/page/loaders/overview-page-loader.server';
import { useMatchesHandleData } from '~/modules/root/hook';
import {
    NumberedAccordion,
    type NumberedAccordionRow,
} from '~/ui-rocco/pages/detail-shared';
import { TestimonialsRow } from './TestimonialsRow';

/** The preview's rows are held open, so a toggle can never reach this. */
const NOOP_VALUE_CHANGE = () => undefined;

/**
 * About tab body — the numbered accordion after About the practice, its
 * awards and its team: the clients' testimonials. Self-hides with no content.
 */
export const ProfessionalAboutContentSection = () => {
    const { professionalOverview } = useLoaderData<typeof loader>();
    const isBMPreview = useMatchesHandleData('isBMPreview', false);

    const testimonials = [
        ...(professionalOverview.professionalTestimonials ?? []),
    ]
        .filter((testimonial) => testimonial.Content)
        .sort((a, b) => (a.MemberSortOrder ?? 0) - (b.MemberSortOrder ?? 0));

    // About the practice, its awards and its team are the sections around
    // this accordion — see ProfessionalAboutTabSection and the two after it.
    const rows: NumberedAccordionRow[] = [];
    // Closes the accordion: the overview leads with the work, and the clients'
    // account of it reads as the last word on the practice rather than an
    // opening claim.
    if (testimonials.length > 0) {
        rows.push({
            title: 'What our Clients say',
            content: <TestimonialsRow testimonials={testimonials} />,
        });
    }

    if (rows.length === 0) return null;

    // On mobile the accordion is the first content element (the intro + summary
    // above are desktop-only), so its leading top border duplicates the sub-nav's
    // bottom border — drop it on mobile, keep it under the desktop summary block.
    const [firstRow] = rows;
    if (firstRow) {
        firstRow.itemClassName = cn(
            firstRow.itemClassName,
            'max-md:border-t-0',
        );
    }

    const defaultOpen = rows[0]?.title;

    // Business Manager's preview is deliberately not clickable, so a collapsed
    // row is a row the author cannot reach — they would be editing their awards
    // with no way to see them rendered. Hold every row open there instead
    // (controlled, so a row that appears mid-edit opens too). The visitor still
    // gets the real default of one open row.
    const openRows = isBMPreview ? rows.map((row) => row.title) : undefined;

    return (
        <section className="bg-ground">
            <NumberedAccordion
                headingLevel={2}
                rows={rows}
                theme="light"
                multiple
                className="md:px-[3.125rem]"
                capClassName="mx-auto w-full max-w-[113.75rem]"
                triggerClassName="md:[&>svg]:size-8"
                value={openRows}
                onValueChange={NOOP_VALUE_CHANGE}
                defaultOpenTitles={defaultOpen ? [defaultOpen] : undefined}
            />
        </section>
    );
};
